import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import {
  SchoolYearModel,
  SchoolYearFilter,
  CreateSchoolYearPayload,
  UpdateSchoolYearPayload
} from '../../models/school/school-year.model';
import { SchoolModel } from '../../models/school/school.model';
import { StringExtensionModel } from '../../models/extensions/string-extension.model';

@Injectable({
  providedIn: 'root'
})
export class AdminSchoolYearService {

  constructor(private authSv: AuthService) {}

  getSchoolYears(filters?: SchoolYearFilter): Observable<SchoolYearModel[]> {
    let url = '/admin/schoolYears';
    if (filters) {
      url += StringExtensionModel.queryString(filters);
    }
    return this.authSv.get(url).pipe(map(res => SchoolYearModel.generateList(res.data)));
  }

  getSchoolYear(id: string): Observable<SchoolYearModel> {
    return this.authSv.get(`/admin/schoolYears/${id}`).pipe(map(res => SchoolYearModel.generate(res.data)));
  }

  createSchoolYear(payload: CreateSchoolYearPayload): Observable<SchoolYearModel> {
    return this.authSv.post('/admin/schoolYears', payload).pipe(map(res => SchoolYearModel.generate(res.data)));
  }

  updateSchoolYear(id: string, payload: UpdateSchoolYearPayload): Observable<SchoolYearModel> {
    return this.authSv.patch(`/admin/schoolYears/${id}`, payload).pipe(map(res => SchoolYearModel.generate(res.data)));
  }

  getSchoolYearSchools(schoolYearId: string, sortByName: 1 | -1 = 1): Observable<SchoolModel[]> {
    return this.authSv.get(`/admin/schoolYears/${schoolYearId}/schools?sortByName=${sortByName}`).pipe(
      map(res => {
        const list = res.data || [];
        return SchoolModel.generateList(list);
      })
    );
  }

  removeSchoolFromSchoolYear(schoolId: string, schoolYearId: string): Observable<SchoolModel> {
    return this.authSv.delete(`/admin/schools/${schoolId}/schoolYears/${schoolYearId}`).pipe(
      map(res => SchoolModel.generate(res.data))
    );
  }

  bulkRemoveSchoolFromSchoolYear(schoolIds: string[], schoolYearId: string): Observable<{ removed: number, failed: number }> {

    if (!schoolIds.length) {
      return of({ removed: 0, failed: 0 });
    }

    const calls = schoolIds.map(id =>
      this.removeSchoolFromSchoolYear(id, schoolYearId).pipe(
        map(() => ({ failed: false })),
        catchError(() => of({ failed: true }))
      )
    );


    return forkJoin(calls).pipe(
      map(results => ({
        removed: results.filter(r => !r.failed).length,
        failed:  results.filter(r => r.failed).length
      }))
    );
  }

  associateSchoolYear(schoolId: string, schoolYearId: string): Observable<{ data: SchoolModel, skipped: boolean }> {
    return this.authSv.post(`/admin/schools/${schoolId}/schoolYears/${schoolYearId}`, { schoolYearId }).pipe(
      map(res => ({ data: SchoolModel.generate(res.data.school), skipped: res.data.skipped === true }))
    );
  }

  bulkAssociateSchoolYear(schoolIds: string[], schoolYearId: string): Observable<{ associated: number, skipped: number, failed: number }> {

    if (!schoolIds.length) {
      return of({ associated: 0, skipped: 0, failed: 0 });
    }

    const calls = schoolIds.map(id =>
      this.associateSchoolYear(id, schoolYearId).pipe(
        map(res => ({ ...res, failed: false })),
        catchError(() => of({ data: null, skipped: false, failed: true }))
      )
    );

    return forkJoin(calls).pipe(
      map(results => ({
        associated: results.filter(r => !r.skipped && !r.failed).length,
        skipped:    results.filter(r => r.skipped).length,
        failed:     results.filter(r => r.failed).length
      }))
    );
  }
}
